/**
 * `src/engine` public surface — the WebGPU renderer (TypeGPU 0.12.7). Owner: Engine (F2; P10 owns `materials/`, `vfx/`,
 * `portrait/`, `triptych/`, `lite/`). Implements `Renderer` from `src/shared/render.ts` (architecture §3.9) following
 * docs/tech/typegpu-cheatsheet.md §3.10 and art-direction §3–§4.
 *
 * - `createRenderer(opts)` — throws `WebGpuUnavailableError` (any reason → lite mode «Без миниатюр», architecture §11);
 * - `onRendererDeviceEvent(r, fn)` — device lost / restored / fatal (second loss within 60 s → lite) for F1's fallback UI;
 * - `createRenderDebug(r)` — the `window.__marg.render` namespace (art §12) for F1's debug API;
 * - `mountArtLab(host, canvas, params, deps)` — the art lab (`?screen=artlab&lab=keyframe&kf=fight`, LG-1).
 */
import type { CreateRendererFn, Renderer, RendererOptions } from '../shared';
import { EngineRenderer, WebGpuUnavailableError, createEngineRenderer, type DeviceEvent } from './renderer';

export { WebGpuUnavailableError, EngineRenderer, type DeviceEvent };
export { createRenderDebug, type RenderDebugNamespace } from './debug';
export { mountArtLab, fallbackLayout, type ArtLab, type ArtLabDeps, type LabLayout, type LabLayoutFn } from './lab/artlab';
export type { CreatureSource, KeyframeId } from './lab/staging';
export { TIER_SETTINGS, AutoTier, deviceDefaultTier, type TierSettings } from './quality';
export { cameraMatrices, projectPoint, arenaFraming } from './camera';

/** Creates the one renderer (single device). Throws {@link WebGpuUnavailableError}. */
export const createRenderer: CreateRendererFn = async (opts: RendererOptions): Promise<Renderer> => {
  if (typeof navigator === 'undefined' || !('gpu' in navigator)) throw new WebGpuUnavailableError('no-api');
  return createEngineRenderer(opts);
};

/** Subscribes to device events of an engine renderer (no-op for other implementations). */
export function onRendererDeviceEvent(r: Renderer, fn: (e: DeviceEvent) => void): () => void {
  return r instanceof EngineRenderer ? r.onDeviceEvent(fn) : () => undefined;
}

export * from './atelier-api';
